import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";
import * as Sentry from "@sentry/react";
import type { FC } from "react";

/**
 * Wraps a tool renderer in an error boundary so a malformed tool call degrades
 * to an inline notice instead of crashing the whole message or transcript.
 */
export function guardToolRender(Component: ToolComponent): ToolComponent {
  return function GuardedTool(props) {
    return (
      <Sentry.ErrorBoundary fallback={<ToolRenderError />}>
        <Component {...props} />
      </Sentry.ErrorBoundary>
    );
  };
}

/** {@link guardToolRender} applied to every renderer in a registry. */
export function guardToolRenders(
  components: Readonly<Record<string, ToolComponent>>,
): Record<string, ToolComponent> {
  return Object.fromEntries(
    Object.entries(components).map(([toolName, Component]) => [
      toolName,
      guardToolRender(Component),
    ]),
  );
}

const ToolRenderError: FC = () => (
  <ChatToolBase status={{ type: "incomplete" }}>
    Unable to display this tool result.
  </ChatToolBase>
);
